<template>
  <ContentContainer :loadingStatus="isLoading">
    <div class="user-list content-wrapper">
      <div class="page-header">{{ $t('userList') }}</div>
      <div>
        <el-row>
          <el-col :span="4">
            <el-input :placeholder="$t('keyword')"
                      @input="isSearching = true"
                      v-model="keyword"></el-input>
          </el-col>
          <el-col :span="20">
            <router-link to="/user/add" style="float: right">
              <el-button type="primary" icon="el-icon-plus">{{ $t('newUser') }}</el-button>
            </router-link>
          </el-col>
        </el-row>
        <div style="margin-top: 25px">
          <el-table
            id="user-list-table"
            :data="userListDataSource"
            v-loading="isSearching"
            header-row-class-name="table-header-color"
            style="width: 100%">
            <el-table-column
              prop="username"
              :label="$t('username')"
              width="180">
            </el-table-column>
            <el-table-column
              :label="$t('fullName')"
              width="180">
              <template slot-scope="scope">{{ bindFullName(scope.row) }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('status')">
              <template slot-scope="scope">
                <el-tag
                  :type="scope.row.isEnable === 1 ? 'success' : 'danger'" size="mini"
                  disable-transitions>{{ getUserStatus(scope.row.isEnable) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column>
              <template slot-scope="scope">
                <el-button size="small" icon="el-icon-key" plain @click="onResetPasswordButtonClick(scope.row)">{{ $t('resetPassword') }}</el-button>
                <el-button type="primary" size="small" icon="el-icon-edit" plain circle @click="onEditButtonClick(scope.row)"></el-button>
                <el-button type="danger" size="small" icon="el-icon-delete" plain circle @click="onRemoveUserButtonClick(scope.row)"></el-button>
              </template>
            </el-table-column>
          </el-table>
          <div style="margin-top: 10px">
            <el-pagination
              background
              layout="prev, pager, next"
              :total="filterUser().length"
              :page-size="pageSize"
              :pager-count="11"
              :current-page="currentPage"
              @current-change = "changePage">
            </el-pagination>
          </div>
        </div>
      </div>
    </div>
  </ContentContainer>
</template>

<script>
import { STATUS } from '@/configs/constant'
import i18nMessages from '@/translations/User/List.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import UserApi from '@/api/user-api'
import ContentContainer from '@/components/ContentContainerComponent.vue'
import { filterDataByKeyword, getDataOnPaging, adjustCurrentPage } from '@/services/general-service'
import { getStatusName, bindData } from '@/helpers'
import { debounce } from 'lodash'

export default {
  name: 'UserList',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  components: { ContentContainer },
  i18n: { messages: i18nMessages },
  watch: {
    keyword: debounce(function () {
      this.isSearching = false
    }, 300),
    isSearching: function (value) {
      if (!value) {
        this.getUserListDataSource()
      }
    }
  },
  created: async function () {
    try {
      this.isLoading = true

      this.userList = await UserApi.list()
      this.getUserListDataSource()
    } catch (err) {
      this.alertError(err.message)
    }

    this.isLoading = false
  },
  data () {
    return {
      STATUS,
      isLoading: false,
      keyword: '',
      isSearching: false,
      userList: [],
      currentPage: 1,
      pageSize: 10,
      userListDataSource: []
    }
  },
  methods: {
    onEditButtonClick: function (user) {
      this.$router.push({
        name: 'UserEdit',
        params: {
          user: user
        }
      })
    },
    onResetPasswordButtonClick: function (user) {
      this.$router.push({
        name: 'UserResetPassword',
        params: {
          user: user
        }
      })
    },
    getUserStatus: function (status) {
      return this.$t(getStatusName(status))
    },
    changePage: function (page) {
      this.currentPage = page
      this.getUserListDataSource()
    },
    getUserListDataSource: function () {
      const filteredUserList = this.filterUser()
      this.currentPage = adjustCurrentPage(filteredUserList.length, this.currentPage, this.pageSize)

      const userList = getDataOnPaging(filteredUserList, this.currentPage, this.pageSize)
      this.userListDataSource = [...userList]
    },
    filterUser: function () {
      const searchKeys = ['username', 'firstName', 'lastName', 'email']
      const filteredUserList = filterDataByKeyword(this.userList, this.keyword, searchKeys)

      return filteredUserList
    },
    onRemoveUserButtonClick: async function (row) {
      const confirmMessage = this.$t('confirmRemoveUserMessage', {
        username: row.username
      })
      const confirmButtonText = this.$t('confirmButtonText')
      const cancelButtonText = this.$t('cancelButtonText')
      const isConfirmRemove = await this.alertConfirmDelete(confirmMessage, confirmButtonText, cancelButtonText)

      if (!isConfirmRemove) {
        return false
      }

      this.removeUser(row.id, row.username)
    },
    removeUser: async function (userId, username) {
      try {
        this.startLoading()

        await UserApi.remove(userId)

        this.userList = this.userList.filter(item => item.id !== userId)
        this.getUserListDataSource()

        const notificationTitle = this.$t('removeUserSuccess', {
          username: username
        })
        this.showNotificationSuccess(notificationTitle)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    bindFullName: function (row) {
      const firstName = bindData(row, 'firstName', '')
      const lastName = bindData(row, 'lastName', '')
      const fullName = `${firstName} ${lastName}`
      return (fullName.trim() === '') ? '-' : fullName
    }
  }
}
</script>

<style>
  #user-list-table {
    width: 100%;
    border: 1px solid #dddddd;
    border-radius: 5px;
    box-shadow: 0px 3px 5px 1px #eeeeee;
  }
</style>
